// React and React Router
import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

// Global styles
import "./App.css";

// Core layout components
import Header from "./MobileComponents/Header/Header";
import Footer from "./Components/Footer/Footer";
import UserPortal from "./Components/UserPortalPage/UserPortal";
import { Email } from "./Components/EmailPage/Email/Email";

// State management (Redux + helpers)
import { useAppDispatch } from "./services/StoreHelper";
import { update_state_value } from "./store/reducers/AppReducer";

// Services and utilities
import {
  decode_jwt,
  getValidAuthToken,
  handleTokenExpired,
  hasUsableUserToken,
  set_dimensions,
} from "./services/global.services";
import { get_club, get_user_by_id } from "./services/api.calls.services";
import { isParentDomainValid } from "./services/validateParentDomain";

function App() {
  const dispatch = useAppDispatch();
  const [local_user, setLocalUser] = useState(() =>
    JSON.parse(localStorage.getItem("user") || "null")
  );

  const dispatchState = (key: string, value: any) =>
    dispatch(update_state_value({ key, value }));

  const fetchClub = async (token: string, clubId: string) => {
    const clubData = await get_club(token, clubId);
    const spanishSpeakingCountryCodes = [
      "AR",
      "BO",
      "CL",
      "CO",
      "CR",
      "CU",
      "DO",
      "EC",
      "SV",
      "GQ",
      "ES",
      "GT",
      "HN",
      "MX",
      "NI",
      "PA",
      "PY",
      "PE",
      "UY",
      "VE",
    ];
    const club = {
      ...clubData,
      usesSpanish: spanishSpeakingCountryCodes.includes(clubData.country),
    };
    dispatchState("club", club);
    localStorage.setItem("club", JSON.stringify(club));
    localStorage.setItem("clubId", JSON.stringify(clubId));
  };

  const fetchUser = async (token: string, userId: string, tokenData: any) => {
    const userData = await get_user_by_id(token, userId);
    setLocalUser(userData);
    dispatchState("user", userData);
    if (tokenData) {
      dispatchState("user_token", tokenData);
      localStorage.setItem("user_token", JSON.stringify(tokenData));
    }
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const clearAppStorage = () => {
    ["app_token", "club", "user", "user_token"].forEach((key) =>
      localStorage.removeItem(key)
    );
  };

  const initFromMessage = async ({ data, origin }: MessageEvent) => {
    if (!isParentDomainValid(origin)) {
      console.log("not valid origin " + origin);
      return;
    }

    console.error("full token data from iframe", data);

    const { token, clubId, userToken, errorUserAccessToken } = data;
    if (!token || !clubId) {
      console.log(`token is ${token} and clubId is ${clubId}`);
      return;
    }

    clearAppStorage();

    // Reset any prior failure so a reload can recover, matching get_agreements.
    dispatchState("app_error", false);

    const tokenData = {
      clubToken: token,
      ...decode_jwt(token),
      ClubId: clubId,
    };

    console.error("club_token =>", `bearer ${token}`); // log clubToken for testing

    handleTokenExpired(tokenData);

    dispatchState("app_token", tokenData);
    localStorage.setItem("app_token", JSON.stringify(tokenData));
    dispatchState("link_expired", !!errorUserAccessToken);

    const promises = [fetchClub(`bearer ${token}`, clubId)];

    console.error("userToken object key exists", userToken);
    console.error(
      userToken && !Object.keys(userToken).length
        ? "userToken object key exists but is not set"
        : "userToken object key exists but is set"
    );

    if (hasUsableUserToken(userToken)) {
      const userJwt = decode_jwt(userToken.access_token);
      const mergedToken = {
        ...userToken,
        ...userJwt,
        UserId: userJwt?.userId || userJwt?.UserId,
        received_at: Math.floor(Date.now() / 1000),
      };

      const authToken = getValidAuthToken(mergedToken);

      console.error("userToken && mergedToken object key exists", mergedToken);
      console.error(
        mergedToken && !Object.keys(mergedToken).length
          ? "mergedToken object key exists but is not set"
          : "mergedToken object key exists but is set"
      );

      console.error("user_token =>", `bearer ${authToken}`); // log userToken for testing

      promises.push(
        fetchUser(`bearer ${authToken}`, mergedToken.UserId, mergedToken)
      );
    }

    try {
      await Promise.all(promises);
    } catch (e) {
      // Flag the failure so the UI shows an error instead of spinning forever on
      // `!club?.clubId`. Do not log the error/response body (may carry PII).
      dispatchState("app_error", true);
    }
  };

  useEffect(() => {
    const resize = () => dispatchState("app_dimensions", set_dimensions());

    const startApp = () => {
      window.addEventListener("message", initFromMessage);
      resize();
      window.addEventListener("resize", resize);
      window.parent.postMessage({ status: "ready" }, "*");
    };

    startApp();

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("message", initFromMessage);
    };
  }, []);

  const hasUserId = local_user?.UserID || local_user?.userId;

  return (
    <div className="App">
      <Header />
      <Router>
        <Routes>
          <Route path="/" element={!hasUserId ? <Email /> : <UserPortal />} />
          <Route path="/email" element={<Email />} />
          <Route path="*" element={!hasUserId ? <Email /> : <UserPortal />} />
        </Routes>
      </Router>
      <Footer />
    </div>
  );
}

export default App;